import { Link } from "react-router-dom";
import type { Media } from "@/types/media";
import { categoryMeta, formatHot } from "@/lib/media";

interface MediaCardProps {
  media: Media;
  rank?: number;
}

export default function MediaCard({ media, rank }: MediaCardProps) {
  const category = categoryMeta[media.category];

  return (
    <Link
      to={`/detail/${media.id}`}
      className="group block animate-fade-up"
      aria-label={`查看《${media.title}》详情`}
    >
      <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-100 border border-background-200/70">
        <img
          src={media.cover}
          alt={`${media.title} 海报`}
          title={`${media.title} ${media.genre} 在线观看`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-background-100/10 to-transparent opacity-90" />

        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-background-50/80 backdrop-blur text-[11px] font-medium text-foreground-800">
          {media.region}
        </span>

        {media.badge && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-[11px] font-bold">
            {media.badge}
          </span>
        )}

        {typeof rank === "number" && (
          <span className="absolute bottom-2 left-2 w-8 h-8 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-heading font-extrabold text-base">
            {rank}
          </span>
        )}

        <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-background-50/80 backdrop-blur text-[11px] font-bold text-accent-600 flex items-center gap-1">
          <i className="ri-star-fill text-accent-500 text-[10px]" />
          {media.rating.toFixed(1)}
        </span>

        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-background-100/90 flex flex-col justify-end p-3">
          <span className="text-[11px] font-medium text-primary-400 mb-1">{category.title} · {media.genre}</span>
          <p className="text-xs leading-relaxed text-foreground-700 line-clamp-4">{media.summary}</p>
          <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-foreground-900">
            查看详情
            <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>

      <div className="mt-2.5">
        <h3 className="text-sm font-medium text-foreground-950 line-clamp-1 group-hover:text-primary-400 transition-colors">
          {media.title}
        </h3>
        <p className="mt-1 text-xs text-foreground-500 line-clamp-1">
          {media.year} · {media.genre}
        </p>
        <p className="mt-1 text-[11px] text-foreground-500 flex items-center gap-1">
          <i className="ri-fire-line text-primary-500" />
          热度 {formatHot(media.hot)}
        </p>
      </div>
    </Link>
  );
}